import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

interface AsyncValidationErrors {
  [fieldName: string]: string | null;
}

interface AsyncValidationContextType {
  errors: AsyncValidationErrors;
  pendingValidations: Set<string>;
  setError: (field: string, error: string | null) => void;
  clearError: (field: string) => void;
  clearAllErrors: () => void;
  setPending: (field: string, isPending: boolean) => void;
  hasAnyPendingValidations: boolean;
}

const AsyncValidationContext = createContext<
  AsyncValidationContextType | undefined
>(undefined);

export const useAsyncValidation = () => {
  const context = useContext(AsyncValidationContext);
  if (!context) {
    throw new Error(
      'useAsyncValidation must be used within AsyncValidationProvider',
    );
  }
  return context;
};

interface AsyncValidationProviderProps {
  children: React.ReactNode;
}

export const AsyncValidationProvider: React.FC<
  AsyncValidationProviderProps
> = ({ children }) => {
  const [errors, setErrors] = useState<AsyncValidationErrors>({});
  const [pendingValidations, setPendingValidations] = useState<Set<string>>(
    new Set(),
  );

  const setError = useCallback((field: string, error: string | null) => {
    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));
  }, []);

  const clearError = useCallback((field: string) => {
    setErrors((prev) => {
      const { [field]: removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrors({});
  }, []);

  const setPending = useCallback((field: string, isPending: boolean) => {
    setPendingValidations((prev) => {
      const newSet = new Set(prev);
      if (isPending) {
        newSet.add(field);
      } else {
        newSet.delete(field);
      }
      return newSet;
    });
  }, []);

  const hasAnyPendingValidations = pendingValidations.size > 0;

  const value: AsyncValidationContextType = useMemo(
    () => ({
      errors,
      pendingValidations,
      setError,
      clearError,
      clearAllErrors,
      setPending,
      hasAnyPendingValidations,
    }),
    [
      errors,
      pendingValidations,
      setError,
      clearError,
      clearAllErrors,
      setPending,
      hasAnyPendingValidations,
    ],
  );

  return (
    <AsyncValidationContext.Provider value={value}>
      {children}
    </AsyncValidationContext.Provider>
  );
};
